import React, { useCallback } from 'react';
import { Button, Modal, Spin, message } from 'antd';
import s from './s.module.less';
import AiTextEditor from 'components/AI/AiTextEditor';
import { EAIEditorType } from 'types/ai';
import { TServerCalendarAppointmentEventDetail } from 'types/calendar';
import useProspectInfo from 'hooks/useProspectInfo';
import useTimezone from 'hooks/useTimezone';
import { SUPPORT_TIME_ZONE } from 'constants/common';
import dayjs from 'utils/dayjs';
import { getTimeStr } from 'utils/calendar';
import { sendMessage } from 'api/inbox';

type IProps = {
    onClose: () => void;
    open: boolean;
    appointmentDetail?: TServerCalendarAppointmentEventDetail;
    onSuccess: () => void;
};

const SendMessageToPatientModal = (props: IProps) => {
    const { open, onClose, appointmentDetail, onSuccess } = props;
    const [content, setContent] = React.useState('');
    const [client] = useProspectInfo(appointmentDetail?.clientId);
    const name = `${client?.firstName} ${client?.lastName}`;
    const [, myTimezone] = useTimezone();
    const [sending, setSending] = React.useState(false);
    let targetTimezone:
        | undefined
        | {
              dictKey: string;
              offset: string;
          };
    //not same as browser timezone
    if (myTimezone?.value) {
        targetTimezone = SUPPORT_TIME_ZONE.find(
            (item) => item.dictKey.toLowerCase() === (myTimezone?.value as string).toLowerCase(),
        );
    }
    const timezone = targetTimezone?.dictKey || undefined;
    let appointmentViewTimeLabel = '';
    let appointmentViewDateLabel = '';
    if (appointmentDetail) {
        appointmentViewTimeLabel = `${dayjs(appointmentDetail.startAt).format(
            dayjs(appointmentDetail.startAt)?.hour() === 0 ? 'HH:mm' : 'hh:mm',
        )} - ${dayjs(appointmentDetail.endAt).format('hh:mm A')}`;
        appointmentViewDateLabel = getTimeStr(dayjs(appointmentDetail?.startAt), 'dddd, MMMM D, YYYY');
        if (timezone) {
            appointmentViewTimeLabel = `${dayjs(appointmentDetail.startAt)
                .tz(timezone)
                .format(dayjs(appointmentDetail.startAt).tz(timezone)?.hour() === 0 ? 'HH:mm' : 'hh:mm')} - ${dayjs(
                appointmentDetail.endAt,
            )
                .tz(timezone)
                .format('hh:mm A')}`;
            appointmentViewDateLabel = getTimeStr(dayjs(appointmentDetail?.startAt), 'dddd, MMMM D, YYYY');
        }
    }

    React.useEffect(() => {
        if (client) {
            setContent(`Hi ${client?.firstName},

I am completing my previous appointment and will join your visit in just a moment. Thank you for waiting.

Best,
${appointmentDetail?.providerFirstName} ${appointmentDetail?.providerLastName}
            `);
        }
    }, [client, appointmentDetail]);

    const handleSend = async () => {
        setSending(true);
        const res = await sendMessage({
            providerId: appointmentDetail?.providerId!,
            clientId: appointmentDetail?.clientId!,
            message: content,
        });
        if (!res.error) {
            message.success('Message sent successfully');
            onSuccess?.();
        }
        setSending(false);
    };

    return (
        <Modal
            className={s.modal}
            width={992}
            open={open}
            title="Send a quick message to your next patient"
            okText="Send"
            onOk={handleSend}
            onCancel={onClose}
            okButtonProps={{
                loading: sending,
            }}
        >
            <div className={s.wrap}>
                <h3>
                    This message will be sent to the patient waiting for your next visit. You can edit it before
                    sending.
                </h3>
                <div className={s.info}>
                    <div className={s.title}>{appointmentDetail?.appointmentName}</div>
                    <div className={s.des}>
                        {name}
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <path
                                d="M9 6V9L11.25 11.25M15.75 9C15.75 12.7279 12.7279 15.75 9 15.75C5.27208 15.75 2.25 12.7279 2.25 9C2.25 5.27208 5.27208 2.25 9 2.25C12.7279 2.25 15.75 5.27208 15.75 9Z"
                                stroke="#6B7280"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        {appointmentViewTimeLabel} {myTimezone?.value}
                        {' | '}
                        {appointmentDetail?.duration}
                        {' mins'}
                    </div>
                </div>
                <div className={s.input}>
                    <div className={s.inputTitle}>Message</div>
                    <AiTextEditor
                        rows={3}
                        onChange={(e) => {
                            if (typeof e === 'string') {
                                setContent(e as string);
                            } else {
                                setContent(e ? e.target.value : '');
                            }
                        }}
                        value={content}
                        type={EAIEditorType.PROSPECT}
                        maxLength={3000}
                        narrow={false}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default SendMessageToPatientModal;
